import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setStrategyCrumb } from '../../../redux/actions';

import { dateService } from '../../../services/date';
import { session } from '../../../services/session';
import { api } from '../../../services/api';
import checkmarkSVG from '../../../images/icons/icon-checkmark-black.svg';
import liveSVG from '../../../images/icons/live.svg';
import dotSVG from '../../../images/icons/dot.svg';
import grayDotSVG from '../../../images/icons/dot-gray.svg';
import { isEvergreen } from './utils';
import DetailBanner from './detail-banner';
import StrategyPerformanceSummary from './strategy-performance-summary';
import { TopPublishersTab, TopStoriesTab, TopRecommendationsTab } from './strategy-wrappers';

import './strategy-performance.scss';

const conversionTypeLabels = {
  ImpactRadiusSubIds: 'Impact Radius',
  Linkshare: 'Linkshare',
  PepperJam: 'PepperJam',
  Default: 'JSTag',
};

const tabsList = [
  { name: 'summary', title: 'Performance Overview' },
  { name: 'publishers', title: 'Top Publishers' },
  { name: 'stories', title: 'Top Stories' },
  { name: 'recommendations', title: 'Top Recommendations' },
];

const componentMap = {
  publishers: TopPublishersTab,
  stories: TopStoriesTab,
  recommendations: TopRecommendationsTab,
};

let futureLimit;
let defaultDates;

function StrategyPerformance({ strategyId, tab = 'summary' }) {
  /* STATE */
  const [overviewData, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const dispatch = useDispatch();
  const campaignPageUrl = `/v2/merchant/${session.orgId}/megatables/campaign-performance`;
  const endpoint = `/api/v0/merchants/${session.orgId}/strategies/${strategyId}/overview/`;

  useEffect(() => {
    api.get(endpoint).then(resp => {
      const { stats } = resp.data.data[0];
      const config = stats.affiliate_config || {};
      stats.conversions_captured = conversionTypeLabels[config.default] || 'JSTag';
      setOverview(stats);
      defaultDates = getDefaultDates(stats);
      setLoading(false);
    });
  }, []);

  /* METHODS */
  function getDefaultDates(stats) {
    const startDate = isEvergreen(strategyId)
      ? dateService.getFirstOfMonth(moment())
      : moment.utc(stats.datetime_start);
    let endDate = moment();

    if (stats.strategy_status === 'expired') {
      endDate = moment.utc(stats.datetime_end);
    }

    futureLimit = endDate;

    return {
      startDate,
      endDate,
    };
  }

  function getStatusAndImg() {
    return !overviewData.strategy_status
      ? {}
      : {
          expired: {
            statusImg: checkmarkSVG,
            statusText: 'Completed',
          },
          upcoming: {
            statusImg: dotSVG,
            statusText: 'Upcoming',
          },
          running_and_paused: {
            statusImg: grayDotSVG,
            statusText: 'Paused',
          },
          running_and_active: {
            statusImg: liveSVG,
            statusText: 'Live',
          },
        }[overviewData.strategy_status];
  }

  /* RENDER */
  if (loading) return null;

  let additionalStatus = null;
  const today = moment();
  const dateStart = isEvergreen(strategyId)
    ? 'Ongoing'
    : moment.utc(overviewData.datetime_start).format('MMM D, YYYY');
  const dateEnd = overviewData.datetime_end
    ? `- ${moment.utc(overviewData.datetime_end).format('MMM D, YYYY')}`
    : '- No end date';
  const { statusImg, statusText } = getStatusAndImg();

  if (overviewData.strategy_status === 'running_and_active' && overviewData.datetime_end) {
    const remainingTime =
      dateService.subtractDates(today, moment(overviewData.datetime_end), 'day') - 1;
    additionalStatus = `(${Math.floor(remainingTime)} days left)`;
  }

  if (overviewData.strategy_status === 'running_and_paused' && overviewData.paused_on) {
    additionalStatus = `as of ${moment(overviewData.paused_on).format('MMM D, YYYY')}`;
  }

  const detailBanner = session.isAdminView ? (
    <DetailBanner strategyId={strategyId} propData={overviewData} />
  ) : null;

  const pastDateThreshold = isEvergreen(strategyId) ? null : moment(overviewData.datetime_start);

  const TabContent = componentMap[tab];
  const content =
    tab === 'summary' ? (
      <StrategyPerformanceSummary
        strategyId={strategyId}
        defaultDates={defaultDates}
        pastDateThreshold={pastDateThreshold}
        futureLimit={futureLimit}
      />
    ) : (
      <TabContent
        strategyId={strategyId}
        type={tab}
        defaultDates={defaultDates}
        pastDateThreshold={pastDateThreshold}
        futureLimit={futureLimit}
      />
    );

  const tabNav = session.isAdminView ? (
    <div className="tabs">
      {tabsList.map(item => {
        const destination =
          `/v2/merchant/${session.orgId}/megatables` +
          `/campaign-performance/${strategyId}/${item.name}`;
        return (
          <div
            key={`tab-${item.name}`}
            className={`tab ${item.name === tab ? 'selected' : ''}`}
            onClick={() => {
              dispatch(setStrategyCrumb(null));
              history.push(destination);
            }}
          >
            {item.title}
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="strategy-performance">
      <header>
        <div className="nav">
          <span className="overview-page" onClick={() => history.push(campaignPageUrl)}>
            Your Campaign Performance
          </span>
          <span className="right-arrow" />
          <span className="strategy-page">{overviewData.name || 'Sample Campaign Name Here'}</span>
        </div>
        <div className="strategy-info">
          <h1>{overviewData.name || 'Sample Campaign Name Here'}</h1>
          <div className="strategy-detail">
            <div className="status">
              <img src={statusImg} alt="status" />
              <span className="text">{statusText}</span>
              <span className="subtext">{additionalStatus}</span>
            </div>
            <div className="divider"></div>
            <div className="dates">
              {dateStart} {dateEnd}
            </div>
          </div>
        </div>
        {detailBanner}
      </header>

      {tabNav}
      {content}
    </div>
  );
}

export default StrategyPerformance;
